import { Component, OnInit, Input, SimpleChanges, forwardRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { Browser, BrowserOpenOptions } from '@capacitor/browser';
import { 
    NavController, IonIcon, IonThumbnail, IonSpinner
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { environment } from '@app/environment';
import { 
    MarvDrawerComponent, MarvSectionTitleComponent, MarvFieldComponent, MarvMessageComponent, MarvSubItemComponent, MarvButtonComponent 
} from '@marv/components';
import { MarvFileService, MarvDateService } from '@marv/services';
import { MissionDetails, MissionDetailsWorker, StoreDetailsInfos, StoreDetailsInfosComponent, PermanentPlanningQuestionnaireComponent } from '@app/components';
import { StoreCheckService, OrderService, OperationService, TokenService, LoggedUserService } from '@app/services';
import { Pcheck } from '@app/models';
import { EpochToStringPipe } from '@app/pipes';

@Component({
    selector: 'app-mission-details-infos',
    templateUrl: './infos.component.html',
    styleUrls: ['./infos.component.scss'],
    imports: [
        IonIcon, IonThumbnail, IonSpinner, TranslatePipe, 
        forwardRef(() => MarvDrawerComponent), forwardRef(() => MarvSectionTitleComponent), forwardRef(() => MarvButtonComponent),
        forwardRef(() => MarvFieldComponent), forwardRef(() => MarvMessageComponent), forwardRef(() => MarvSubItemComponent),
        EpochToStringPipe, forwardRef(() => StoreDetailsInfosComponent), forwardRef(() => PermanentPlanningQuestionnaireComponent)
    ]
})
export class MissionDetailsInfosComponent  implements OnInit {

    @Input({alias : 'mission', required : true}) missionDetails: MissionDetails;
    @Output() onUpdateInfos = new EventEmitter<boolean>();

    @ViewChild('drawerPermanentStoreCheckQuestionnaire') drawerPermanentStoreCheckQuestionnaire: MarvDrawerComponent;
    
    environment = environment;
    StoreDetailsInfos = StoreDetailsInfos;
    isFetchingBriefMission: boolean = false;
    isFetchingDownload: number|null = null;
    showBtnEditStoreCheck: boolean = false;
    storeCheckObjectiveId: number|null = null;
    worker: MissionDetailsWorker|null = null;
    showAgentAffected: boolean = false;
            
    constructor(
        private navController: NavController,
        public translateService: TranslateService,
        private storeCheckService: StoreCheckService,
        private orderService: OrderService,
        private tokenService: TokenService,
        public loggedUserService: LoggedUserService
    ) { }

    async ngOnInit() {
        this.showBtnEditStoreCheck = this.missionDetails.customerEditPermissionAllowed;
    }
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['missionDetails']){
            this.showAgentAffected = false;
            let worker: MissionDetailsWorker|null = null;
            if(this.missionDetails){
                if(this.missionDetails.activitySelected){
                    worker = this.missionDetails.activitySelected.worker || null;
                }
                else if(this.missionDetails.worker){
                    worker = this.missionDetails.worker;
                }
                
                if(this.missionDetails.source === 'loga'){
                    if(this.missionDetails.activitySelected){
                        let date = MarvDateService.ToDate(this.missionDetails.activitySelected.day);
                        let times = (this.missionDetails.activitySelected.amStartTime || this.missionDetails.activitySelected.pmStartTime || '').split(':');
                        if(date && times.length === 2){                           
                            let mDate = moment(date);
                            mDate.hours(parseInt(times[0]));
                            mDate.minutes(parseInt(times[1]));
                            let diffHours = mDate.diff(moment(), 'hours');                            
                            let operationSettings = OperationService.OperationsSettings.get(this.missionDetails.operation?.id);
                            this.showAgentAffected = operationSettings?.shareAssignedWorkerInfoByEmail === true && diffHours <= 48;
                        }
                    }
                }
                else{
                    this.showAgentAffected = true;
                }
                
            }
            this.worker = worker;
        }
    }    
    
    isCraDisplayAllowed(): boolean {
        let displayCra: boolean = false;
        if(this.missionDetails.source === 'loga' && this.missionDetails.activitySelected){
            let operationSettings = OperationService.OperationsSettings.get(this.missionDetails.operation?.id);
            if (operationSettings && this.missionDetails.activitySelected.report !== null) {
                if (operationSettings.showCra === "PUBLISHED") {
                    displayCra = this.missionDetails.activitySelected.report.published;
                } 
                else if (operationSettings.showCra === "YES") {
                    displayCra = true;
                }
            }             
        }
        return displayCra;
    }
    
    async openCra(){
        if(this.missionDetails.source === 'loga' && this.missionDetails.activitySelected &&  this.missionDetails.activitySelected.report !== null && this.isCraDisplayAllowed()){            
            let animaToken: string = this.tokenService.buildAnimaToken( this.missionDetails.activitySelected.report.id);
            let options: BrowserOpenOptions = {
                url: environment.ANIMA_REPORTS_URL + "/" +  this.missionDetails.activitySelected.report.id + "/jstart.html?tkn=" + animaToken,
                presentationStyle: 'fullscreen',
                windowName: 'anima_cra'
            };
            await Browser.open(options);
        }
    }
    
    async downloadBriefMission(){
        if(this.missionDetails.briefMission?.file && !this.isFetchingBriefMission){
            this.isFetchingBriefMission = true;        
            let pdfContent = await this.orderService.loadClientBriefMission(this.missionDetails.missionId);            
            if(pdfContent !== null){   
                let filename = 'Brief_Mission.pdf';
                MarvFileService.downloadBlob(filename, pdfContent);
            }
            this.isFetchingBriefMission = false;
        }
    }

    async downloadAllObjectivesReports() {
        this.isFetchingDownload = -1;
        let base64 = await this.storeCheckService.downloadAllObjectivesReports(this.missionDetails.operation.id, this.missionDetails.missionId);
        if(base64 !== null){
            let filename = 'Objectives_' + this.missionDetails.store.name + '_'+ moment().format('DD-MM-YYYY_HH-mm')+'.pdf';
            MarvFileService.downloadByteArray(filename, MarvFileService.base64ToArrayBuffer(base64), "application/pdf");
        }
        this.isFetchingDownload = null;
    }

    async downloadObjectiveReport(objId: number, objName: string) {
        this.isFetchingDownload = objId;
        let base64 = await this.storeCheckService.downloadObjectiveReport(this.missionDetails.operation.id, this.missionDetails.missionId, objId);
        if(base64 !== null){
            let filename = objName + '_' + this.missionDetails.store.name + '_'+ moment().format('DD-MM-YYYY_HH-mm')+'.pdf';
            MarvFileService.downloadByteArray(filename, MarvFileService.base64ToArrayBuffer(base64), "application/pdf");
        }
        this.isFetchingDownload = null;
    }
     
    openStoreCheckQuestionnaire($event: any, objectiveId: number){
        $event.stopPropagation();
        this.storeCheckObjectiveId = objectiveId;
        this.drawerPermanentStoreCheckQuestionnaire.open();
    }
    
    async onDidDismissDrawerPermanentStoreCheckQuestionnaire($event: any){
        this.storeCheckObjectiveId = null;
    } 
    
    async onChangeQuestionnaire(status: boolean){
        await this.drawerPermanentStoreCheckQuestionnaire.close(true);
        this.onUpdateInfos.emit(status);
    }
        
    goToPermanentPlanning(event: any){
        event.stopPropagation();
        let activity = this.missionDetails.activitySelected;
        if(activity){
            let date = MarvDateService.ToDate(activity.day);
            if(date){
                let week = moment(MarvDateService.GetFirstDayOfWeek(date)).format('YYYY-MM-DD');
                this.navController.navigateRoot('permanent-planning', {
                    queryParams: {
                        operationId: this.missionDetails.operation.id, 
                        agentId: this.missionDetails.worker?.id,
                        week: week
                    }
                });
            }
        }
    }
}
